<script setup>
    import Slider from '@vueform/slider'
    import { storeToRefs } from 'pinia'

    defineProps({
        hideRelatedCars: {
            type: Boolean,
            default: false,
        },
    })

    const { data: quoteBuilder } = await useStatamicGlobals().getGlobal({
        handle: 'quote_builder',
    })

    const store = useStore()
    const selectedPeriod = ref('Weekly')
    const salaryInput = ref()
    const { quotes } = storeToRefs(store)
    const { leaseLength } = storeToRefs(store)
    const loading = ref(false)

    const goToEnquire = () => {
        if (store.salary) {
            loading.value = true
            navigateTo('/enquire')
        } else {
            salaryInput.value.focus()
        }
    }

    const kmsPerYearOptions = []

    for (
        let i = quoteBuilder.value.kms_per_year.lower_amount;
        i <= quoteBuilder.value.kms_per_year.upper_amount;
        i += quoteBuilder.value.kms_per_year.step
    ) {
        kmsPerYearOptions.push(i)
    }

    onMounted(() => {
        setTimeout(() => scrollToTop(), 300)
    })

    const scrollToTop = () => {
        const contentEl = document.querySelector('[name=content]')
        const headerHeight = parseInt(
            getComputedStyle(document.querySelector('html'))
                .getPropertyValue('--header-height')
                .replace('px', ''),
        )
        const closeButtonOffset = 60

        window.scrollTo({
            top: contentEl.offsetTop - headerHeight - closeButtonOffset,
            behavior: 'smooth',
        })
    }
</script>

<template>
    <div class="container">
        <QuoteBuilderHeader
            :title="quoteBuilder.step_2_title"
            :currentStep="2"
        />

        <div class="flex w-full flex-col gap-8 md:flex-row md:gap-6 lg:gap-8">
            <div class="selected-car-details flex flex-1 flex-col gap-4 lg:gap-6">
                <figure class="relative aspect-[555/370] w-full">
                    <img
                        :src="store.selectedCar?.feature_image?.permalink ?? ''"
                        class="car-image absolute inset-0 h-full w-full object-cover"
                    />
                </figure>

                <ul class="flex gap-3">
                    <li
                        v-for="(feature, i) in [
                            'Low running cost',
                            'Low weekly rental',
                            store.selectedCar.fbt_exempt ? 'FBT Exempt' : 'Save on tax',
                        ]"
                        :key="i"
                        class="icon icon-down-with-money flex aspect-[247/188] flex-1 flex-col items-center justify-center gap-2 bg-brand1 p-2 text-center text-sm leading-4 text-white"
                    >
                        <span class="mt-12">{{ feature }}</span>
                    </li>
                </ul>
            </div>

            <div class="flex-1">
                <h3 class="text-2xl font-medium">
                    {{ store.selectedCar.title }}
                </h3>

                <p class="text-sm font-medium text-mid">
                    Manufacturer's recommended drive away price
                    {{ prettyPrice(store.selectedCar.price.from) }}
                    <template
                        v-if="
                            store.selectedCar.price.to &&
                            store.selectedCar.price.to >
                                store.selectedCar.price.from
                        "
                    >
                        -
                        {{ prettyPrice(store.selectedCar.price.to) }}
                    </template>
                </p>

                <label class="mt-4 block">
                    <h4 class="text-brand1">Annual salary (excluding super)</h4>
                    <div class="mb-4 text-sm text-mid">
                        {{ prettyPrice(store.salary) }}
                    </div>

                    <div class="slider-wrapper">
                        <Slider
                            v-model="store.salary"
                            :lazy="false"
                            tooltipPosition="bottom"
                            :min="quoteBuilder.salary_amounts.lower_limit"
                            :max="quoteBuilder.salary_amounts.upper_limit"
                            :step="1000"
                            :tooltips="false"
                        />
                    </div>
                </label>

                <label class="mt-4 block">
                    <h4 class="text-brand1">Lease Length</h4>
                    <div class="mb-4 text-sm text-mid">
                        {{ store.leaseLength }} year{{
                            store.leaseLength == 1 ? '' : 's'
                        }}
                    </div>

                    <div class="slider-wrapper">
                        <Slider
                            v-model="store.leaseLength"
                            :lazy="false"
                            tooltipPosition="bottom"
                            :min="quoteBuilder.lease_length.lower_limit"
                            :max="quoteBuilder.lease_length.upper_limit"
                            :step="1"
                            :tooltips="false"
                        />
                        <div
                            class="track-markers flex items-center justify-between"
                        >
                            <span
                                v-for="i in quoteBuilder.lease_length
                                    .upper_limit"
                            ></span>
                        </div>
                    </div>
                </label>

                <label class="mt-4 block">
                    <h4 class="text-brand1">Kms per year</h4>
                    <div class="mb-4 text-sm text-mid">
                        {{ store.kmsPerYear }} km{{
                            store.kmsPerYear == 1 ? '' : 's'
                        }}
                    </div>

                    <div class="slider-wrapper 5-marks">
                        <Slider
                            v-model="store.kmsPerYear"
                            :lazy="false"
                            tooltipPosition="bottom"
                            :min="kmsPerYearOptions[0]"
                            :max="
                                kmsPerYearOptions[kmsPerYearOptions.length - 1]
                            "
                            :step="quoteBuilder.kms_per_year.step"
                            :tooltips="false"
                        />
                        <div
                            class="track-markers flex items-center justify-between"
                        >
                            <span v-for="i in kmsPerYearOptions.length"></span>
                        </div>
                    </div>
                </label>

                <ul
                    class="mt-8 flex overflow-hidden rounded-full border border-brand1 text-center md:inline-flex"
                >
                    <li
                        v-for="period in ['Weekly', 'Fortnightly', 'Monthly']"
                        :key="period"
                        class="flex-1"
                    >
                        <label
                            class="period-item relative block cursor-pointer px-6 py-1 text-center"
                        >
                            <input
                                type="radio"
                                :value="period"
                                v-model="selectedPeriod"
                                class="peer invisible absolute"
                            />

                            <span
                                class="block transition before:absolute before:inset-0 before:-z-10 before:transition peer-checked:text-white peer-checked:before:bg-brand1"
                            >
                                {{ period }}
                            </span>
                        </label>
                    </li>
                </ul>

                <QuoteBuilderAmounts
                    :quote="store.selectedQuote"
                    :period="selectedPeriod"
                />

                <p class="my-3 font-arial text-xs">
                    * Figures are indicative only and may vary slightly due to rounding or calculation differences based on salary, lease term, and kilometres.
                    Final amounts will be confirmed upon receipt of your application.
                </p>

                <h5>What's included?**</h5>

                <ul
                    class="my-1 flex flex-wrap gap-x-4 gap-y-2 font-arial text-sm leading-4 text-dark"
                >
                    <li
                        v-for="inclusion in [
                            'Registration',
                            'Servicing',
                            'Tyres',
                            'Roadside Assist',
                            'Incidentals',
                        ]"
                        class="dotted"
                    >
                        {{ inclusion }}
                    </li>
                </ul>

                <p class="mt-3 font-arial text-xs">
                    ** What's included in your total novated lease payment. For
                    insurance inclusion, please speak to a Younovate consultant
                    for an updated estimate.
                </p>

                <div
                    class="mt-6 flex flex-col items-center gap-3 bg-light px-8 pb-3 pt-6 text-center text-dark"
                    v-if="store.selectedQuote"
                >
                    <h3 class="max-w-[26.6rem] text-lg font-bold">
                        You can expect to save
                        <span class="text-brand1">
                            {{
                                prettyPrice(
                                    store.selectedQuote?.savings?.total || 0,
                                )
                            }}
                        </span>
                        over the course of your lease!
                    </h3>

                    <PrettyButton
                        class="text-base"
                        variant="brand2"
                        @click.prevent="goToEnquire()"
                        :loading="loading"
                    >
                        {{
                            quoteBuilder.step_2_submit_button_text ||
                            'Enquire Now'
                        }}
                    </PrettyButton>

                    <a
                        href="/legal"
                        target="_blank"
                        class="text-[14px] text-brand1 underline"
                    >
                        Terms and Conditions
                    </a>
                </div>
            </div>
        </div>

        <div v-if="!hideRelatedCars">
            <h2 class="my-4">
                More
                <em>Novated Lease options</em>
            </h2>

            <CarsList
                :cars="store.getSimilarlyPricedCars(6)"
                :columns="3"
                @click="scrollToTop()"
            />
        </div>
    </div>
</template>

<style scoped>
    .car-image {
        view-transition-name: car-image;
        contain: layout;
    }

    .slider-wrapper {
        position: relative;

        .track-markers {
            left: 0;
            right: 0;
            top: 50%;
            margin-inline: auto;
            height: 0;
            pointer-events: none;
            position: absolute;
            width: calc(100% - 22px);

            > span {
                --width: 1px;
                --height: 8px;
                content: '';
                @apply bg-dark;
                width: var(--width);
                height: var(--height);
                top: calc(var(--height) / 2 * -1);
                pointer-events: none;

                &:first-child,
                &:last-child {
                    opacity: 0;
                }
            }
        }
    }

    .icon-down-with-money:before {
        content: '';
        mask: url('@/assets/icons/down-with-money.svg') no-repeat top / contain;
        background-color: currentColor;
        @apply absolute;
        @apply -mt-6;
        @apply h-[43px];
        @apply w-[40px];

        @screen sm {
            @apply w-[80px];
        }

        @screen md {
            @apply w-[40px];
        }

        @screen lg {
            @apply w-[60px];
        }

        @screen xl {
            @apply w-[80px];
        }
    }

    .period-item {
        @media (max-width: 380px) {
            @apply px-4;
        }

        @media (max-width: 330px) {
            @apply px-3;
        }
    }

    .text-input {
        border-bottom: 1px solid;
        border-color: var(--mid);
        position: relative;
        white-space: nowrap;

        &.currency {
            &::before {
                content: '$';
                color: var(--mid);
                padding-right: 5px;
            }
        }

        &:focus-within {
            border-color: var(--brand1);
            border-bottom-width: 2px;
        }

        input:focus-visible {
            outline: none;
        }
    }
</style>
